import {
  ColumnFiltersState,
  VisibilityState,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from '@tanstack/react-table';
import useTheme from 'chat-ui/src/hooks/useTheme';
import { useContext, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useDebouncedFilter } from '@/hooks/useDebouncedFilter';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { LangContext } from '@/context/LangProvider';
import useWorkspaceRoles from '@/hooks/useWorkspaceRoles';

import Squares2X2Icon from '../icons/Squares2X2Icon';
import TableCellsIcon from '../icons/TableCellsIcon';
import CreateProjectModal from '../Modals/CreateProjectModal';
import { Skeleton } from '../ui/skeleton';
import MyWorkspaceColumns from '@/components/all-tellets-table/MyWorkspaceColumns';
import DataTablePagination from '../paginations/DataTablePagination';
import { Project, UrlTableProps, Workspace } from '@/types';
import MyWorkspaceGridView from './MyWorkspaceGridView';
import { getPageCount } from '@/utils/getPageCount';

interface MyWorkspaceTableProps extends UrlTableProps {
  mergedWorkspaces: Workspace[];
  projects?: Project[];
  isLoadingProjects: boolean;
  error: Error | null;
  totalCount?: number;
  pageSize: number;
}

export default function MyWorkspaceTable({
  mergedWorkspaces,
  projects,
  isLoadingProjects,
  error,
  urlCurrentPage,
  updateUrlPage,
  urlSorting,
  updateUrlSorting,
  updateUrlFilter,
  getUrlFilterValue,
  totalCount,
  pageSize,
}: MyWorkspaceTableProps) {
  const { isUserOwner, isUserEditor } = useWorkspaceRoles();
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>({});
  const [rowSelection, setRowSelection] = useState({});
  const { lang } = useContext(LangContext);
  const { pathname } = useLocation();
  const [tableLayout, setTableLayout] = useState(() => {
    const savedTableLayout = localStorage.getItem('tableLayout');
    return savedTableLayout || 'list';
  });
  const { currentTheme } = useTheme();

  // Debounced filter for title search
  const { inputValue: titleInputValue, setInputValue: setTitleInputValue } = useDebouncedFilter({
    filterKey: 'title',
    currentValue: getUrlFilterValue('title'),
    onFilterChange: updateUrlFilter,
  });

  const [showSkeleton, setShowSkeleton] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => {
      setShowSkeleton(true);
    }, 150);

    return () => clearTimeout(timer);
  }, []);

  const columns = MyWorkspaceColumns(lang, mergedWorkspaces, isUserOwner, isUserEditor, pathname);

  const changeLayoutTo = (newLayout: string) => {
    localStorage.setItem('tableLayout', newLayout);
    setTableLayout(newLayout);
  };

  const table = useReactTable({
    data: projects ?? [],
    columns,
    pageCount: getPageCount(totalCount, pageSize),
    onSortingChange: updateUrlSorting,
    onColumnFiltersChange: setColumnFilters,
    getCoreRowModel: getCoreRowModel(),
    onColumnVisibilityChange: setColumnVisibility,
    onRowSelectionChange: setRowSelection,
    manualPagination: true,
    manualFiltering: true,
    manualSorting: true,
    state: {
      sorting: urlSorting,
      columnFilters,
      columnVisibility,
      rowSelection,
      pagination: {
        pageIndex: urlCurrentPage - 1,
        pageSize,
      },
    },
  });

  // Sync URL filter values with table columnFilters
  useEffect(() => {
    const titleFilter = getUrlFilterValue('title');
    setColumnFilters(titleFilter ? [{ id: 'title', value: titleFilter }] : []);
  }, [getUrlFilterValue]);

  if (isLoadingProjects && showSkeleton) {
    return (
      <div className="w-full mt-4">
        <div className="flex flex-wrap items-center justify-between w-full gap-4 pt-4 pb-2 md900:w-full md900:gap-2 md900:flex-col">
          <div className="flex items-center gap-2 mb-4 w-fit md900:w-full md900:flex-col">
            <Skeleton className="w-[185px] md900:w-full h-10 rounded-md" />
            <Skeleton className="w-[260px] md900:w-full h-10 rounded-md" />
          </div>
          <div className="flex items-center">
            <Skeleton className="w-[96px] opacity-50 h-10 rounded-l-md rounded-r-none mb-4" />
            <Skeleton className="w-[96px] opacity-100 h-10 rounded-r-md rounded-l-none mb-4" />
          </div>
        </div>
        <Skeleton className="w-full h-96" />
      </div>
    );
  }

  return (
    // eslint-disable-next-line react/jsx-no-useless-fragment
    <>
      {!error && projects && (
        <div className="w-full mt-4">
          <div className="flex flex-wrap items-center justify-between w-full gap-4 pt-1 pb-2 md900:w-full md900:gap-2 md900:flex-col">
            {/* email filter and cols filtering */}
            <div className="flex items-center gap-2 mb-4 w-fit md900:w-full md900:flex-col">
              <CreateProjectModal />
              {/* table name filter */}
              <Input
                placeholder={lang.get('msg.filterByProjectTitle')}
                value={titleInputValue}
                onChange={(event) => {
                  updateUrlPage(1);
                  setTitleInputValue(event.target.value);
                }}
                className="w-64 md900:w-full"
              />
            </div>
            {/* layout btns */}
            <div className="flex items-center mb-4">
              <Button
                style={{
                  backgroundColor:
                    tableLayout === 'grid'
                      ? currentTheme?.primary
                      : currentTheme?.['lighter-primary'],
                  color: tableLayout === 'grid' ? '#FFFFFF' : currentTheme?.['primary-text'],
                }}
                onClick={() => changeLayoutTo('grid')}
                variant="secondary"
                className="flex items-center w-24 gap-1 rounded-r-none hover:text-white"
              >
                <Squares2X2Icon className="size-4" />
                {lang.get('msg.grid')}
              </Button>
              <Button
                style={{
                  backgroundColor:
                    tableLayout === 'list'
                      ? currentTheme?.primary
                      : currentTheme?.['lighter-primary'],
                  color: tableLayout === 'list' ? '#FFFFFF' : currentTheme?.['primary-text'],
                }}
                onClick={() => changeLayoutTo('list')}
                variant="secondary"
                className="flex items-center w-24 gap-1 rounded-l-none hover:text-white"
              >
                <TableCellsIcon className="size-5" />
                {lang.get('msg.list')}
              </Button>
            </div>
          </div>
          {/* -- table layout -- */}
          {tableLayout === 'list' ? (
            <div className="border rounded-md overflow-x-auto">
              <Table className="text-dark-text">
                <TableHeader>
                  {table?.getHeaderGroups()?.map((headerGroup) => (
                    <TableRow key={headerGroup.id}>
                      {headerGroup.headers.map((header) => (
                        <TableHead key={header.id}>
                          {header.isPlaceholder
                            ? null
                            : flexRender(header.column.columnDef.header, header.getContext())}
                        </TableHead>
                      ))}
                    </TableRow>
                  ))}
                </TableHeader>
                <TableBody>
                  {table?.getRowModel()?.rows?.length ? (
                    table?.getRowModel()?.rows?.map((row) => (
                      <TableRow key={row.id} data-state={row.getIsSelected() && 'selected'}>
                        {row.getVisibleCells().map((cell) => (
                          <TableCell key={cell.id}>
                            {flexRender(cell.column.columnDef.cell, cell.getContext())}
                          </TableCell>
                        ))}
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={columns?.length} className="h-24 text-center">
                        {lang.get('msg.noResults')}
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </div>
          ) : (
            <MyWorkspaceGridView
              projects={projects}
              currentTheme={currentTheme}
              mergedWorkspaces={mergedWorkspaces}
              isUserOwner={isUserOwner}
              isUserEditor={isUserEditor}
            />
          )}
          <DataTablePagination
            table={table}
            currentPage={urlCurrentPage}
            onPageChange={updateUrlPage}
          />
        </div>
      )}
    </>
  );
}
